import { resolveMultiFilterLabels } from '#/modules/commonComponents/table/columnFilterUtils';
import { formatApiDateDisplay, formatApiDateRangeDisplay } from '#/utils/date';

function resolveDateFilterDisplay(values) {
  return values.map((value) => {
    if (value && typeof value === 'object') {
      const rangeDisplay = formatApiDateRangeDisplay(value);
      return rangeDisplay || value;
    }
    return formatApiDateDisplay(value) || value;
  });
}

export function createFixedMultiFilterMeta({
  key,
  label,
  options,
  filterValue,
  onChange,
}) {
  return {
    filterVariant: 'selectMulti',
    filterMode: 'server',
    facet: 'fixed',
    multiValue: true,
    options,
    filterStatusKey: key,
    filterStatusLabel: label,
    filterValue,
    onServerFilterChange: onChange,
    resolveFilterDisplay: (values) => resolveMultiFilterLabels(values, options),
  };
}

export function createAsyncSingleFilterMeta({
  key,
  label,
  searchOptions,
  filterValue,
  onChange,
  searchPlaceholder = '搜尋代碼或名稱',
  resolveFilterDisplay,
  rememberSelectedOptions,
}) {
  return {
    filterVariant: 'selectSingle',
    filterMode: 'server',
    facet: 'async',
    multiValue: false,
    searchOptions,
    filterStatusKey: key,
    filterStatusLabel: label,
    filterValue,
    onServerFilterChange: onChange,
    searchPlaceholder,
    ...(resolveFilterDisplay ? { resolveFilterDisplay } : {}),
    ...(rememberSelectedOptions ? { rememberSelectedOptions } : {}),
  };
}

export function createAsyncMultiFilterMeta({
  key,
  label,
  searchOptions,
  filterValue,
  onChange,
  searchPlaceholder = '搜尋代碼或名稱',
  resolveFilterDisplay,
  rememberSelectedOptions,
}) {
  return {
    filterVariant: 'selectMulti',
    filterMode: 'server',
    facet: 'async',
    multiValue: true,
    searchOptions,
    filterStatusKey: key,
    filterStatusLabel: label,
    filterValue,
    onServerFilterChange: onChange,
    searchPlaceholder,
    ...(resolveFilterDisplay ? { resolveFilterDisplay } : {}),
    ...(rememberSelectedOptions ? { rememberSelectedOptions } : {}),
  };
}

export function createTextFilterMeta({
  key,
  label,
  filterValue,
  onChange,
  placeholder,
  debounceMs,
}) {
  return {
    filterVariant: 'text',
    filterMode: 'server',
    filterStatusKey: key,
    filterStatusLabel: label,
    filterValue,
    onServerFilterChange: onChange,
    filterPlaceholder: placeholder ?? `搜尋${label}`,
    ...(debounceMs !== undefined ? { filterDebounceMs: debounceMs } : {}),
  };
}

/**
 * 日期篩選（DatePicker）；value 為 API `YYYY-MM-DD`（single）或 `{ from, to }`（range）。
 */
export function createDateFilterMeta({
  key,
  label,
  filterValue,
  onChange,
  placeholder = '西元年/月/日',
  mode = 'single',
  resolveFilterDisplay = resolveDateFilterDisplay,
}) {
  return {
    filterVariant: 'date',
    filterMode: 'server',
    dateFilterMode: mode,
    filterStatusKey: key,
    filterStatusLabel: label,
    filterValue,
    onServerFilterChange: onChange,
    filterPlaceholder: placeholder,
    resolveFilterDisplay,
  };
}

export function createTreeMultiFilterMeta({
  key,
  label,
  treeRole,
  filterValue,
  onChange,
  searchPlaceholder = '搜尋代號或名稱',
  ...rest
}) {
  return {
    filterVariant: 'selectMulti',
    filterMode: 'server',
    facet: 'tree',
    treeRole,
    multiValue: true,
    filterStatusKey: key,
    filterStatusLabel: label,
    filterValue,
    onServerFilterChange: onChange,
    searchPlaceholder,
    ...rest,
  };
}

/**
 * 數量區間篩選（>0／min／max）；表頭 `ColumnFilterQtyPopover`。
 * 狀態 chip 請於頁面另行掛 `baseQtyGt`／`baseQtyMin`／`baseQtyMax`。
 */
export function createQtyRangeFilterMeta({
  qtyGt,
  qtyMin,
  qtyMax,
  onChange,
  label = '數量',
}) {
  return {
    filterVariant: 'qtyRange',
    filterMode: 'server',
    filterStatusLabel: label,
    qtyGt,
    qtyMin,
    qtyMax,
    onServerQtyFilterChange: onChange,
  };
}

/**
 * 金額區間篩選（min／max，含端點）；沿用 `ColumnFilterQtyPopover`，不顯示「> 0」。
 * onChange 收 `{ min, max }`（字串或 undefined）；狀態 chip 請於頁面另行掛 min／max key。
 */
export function createAmountRangeFilterMeta({
  min,
  max,
  onChange,
  label = '金額',
}) {
  return {
    filterVariant: 'qtyRange',
    filterMode: 'server',
    filterStatusLabel: label,
    showQtyGt: false,
    rangeMinLabel: `最小${label}`,
    rangeMaxLabel: `最大${label}`,
    rangeErrorMessage: `最小${label}不可大於最大${label}`,
    qtyMin: min,
    qtyMax: max,
    onServerQtyFilterChange: ({ baseQtyMin, baseQtyMax }) => onChange({
      min: baseQtyMin,
      max: baseQtyMax,
    }),
  };
}
